import IconButton from 'components/Luxkit/Button/IconButton'
import LineMultiplyIcon from 'components/Luxkit/Icons/line/LineMultiplyIcon'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import useHasMounted from 'hooks/useHasMounted'
import { rem } from 'polished'
import React from 'react'
import cn from 'clsx'
import styled, { keyframes } from 'styled-components'
import Clickable from '../Clickable/Clickable'
import useDelayedTogglingBoolean from 'hooks/useDelayedTogglingBoolean'
import zIndex from 'styles/tools/z-index'
import eases from 'styles/tools/eases'

const ANIMATION_DURATION_MS = 300

// hard code each animation so they have distinct names to avoid animation caching issue
// vertical sliding animation
const animationMobileEntry = keyframes`
  from {
    transform: translate(-50%, 120%);
    visibility: visible;
  }

  to {
    transform: translate(-50%, 0%);
    visibility: visible;
  }
`
const animationMobileExit = keyframes`
  from {
    transform: translate(-50%, 0%);
    visibility: visible;
  }

  to {
    transform: translate(-50%, 120%);
    visibility: hidden;
  }
`

// horizontal sliding animation
const animationDestopEntry = keyframes`
  from {
    transform: translateX(120%);
    visibility: visible;
  }

  to {
    transform: translateX(0%);
    visibility: visible;
  }
`
const animationDestopExit = keyframes`
  from {
    transform: translateX(0%);
    visibility: visible;
  }

  to {
    transform: translateX(120%);
    visibility: hidden;
  }
`

const Container = styled.div`
  ${mediaQueryUp.sm} {
    height: 0;
    width: 100%;
    position: sticky;
    top: 0; // do not delete this
    z-index: ${zIndex.stickyPromptCard};
  }
`

// mobile is shrinked by 20% so we need to compensate
const mobileWidthDesired = 360
const mobileWidth = Math.floor(mobileWidthDesired / 0.8)

const defaultTopMargin = 16

const CardRoot = styled(Clickable)`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: calc(100vw - ${rem(32)});
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  border: ${rem(1)} solid ${({ theme }) => theme.palette.neutral.five};
  box-shadow: ${({ theme }) => theme.shadow.bottom.large};
  border-radius: ${rem(2)};
  flex: none;
  flex-grow: 0;
  
  // mobile
  padding: ${rem(20)};
  width: ${rem(mobileWidth)};
  // desktop
  ${mediaQueryUp.sm} {
    padding: ${rem(16)};
    width: ${rem(300)};
  }
  
  :hover {
    border-color: ${({ theme }) => theme.palette.neutral.one};
  }

  animation-duration: ${ANIMATION_DURATION_MS}ms;
  animation-iteration-count: 1;
  animation-direction: alternate;
  animation-timing-function: ${eases.power2.out};
  visibility: hidden;
  
  &.isEntry {
    visibility: visible;
  }

  ${mediaQueryDown.xs} {
    /* center at bottom */
    z-index: ${zIndex.stickyPromptCard};
    position: fixed;
    bottom: ${rem(8)};
    left: 50%;
    transform: translateX(-50%);
    animation-name: ${animationMobileExit};

    &.isEntry {
      animation-name: ${animationMobileEntry};
    }
  }

  ${mediaQueryUp.sm} {
    /* top right corner */
    position: absolute;
    top: var(--prompt-card-sticky-top);
    right: ${rem(16)};
    animation-name: ${animationDestopExit};
    
    &.isEntry {
      animation-name: ${animationDestopEntry};
    }
  }
`

/** Close button */
const CloseBlock = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: ${rem(24)};
  height: ${rem(24)};
  top: ${rem(16)};
  right: ${rem(16)};
  
  ${mediaQueryUp.sm} {
    top: ${rem(12)};
    right: ${rem(12)};
  }
`

/** Top section */
const HeaderGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0 ${rem(32)} ${rem(8)} 0;
`

/** Middle section */
const ContentGroup = styled.div`
  width: 100%;
`

/** Bottom section */
const FooterGroup = styled.div`
  width: 100%;
`

interface Props {
  show: boolean;
  to: string;
  onClose: () => void;
  onClick: () => void;
  stickyTop?: number;
}

const StickyPromptCard = React.forwardRef<HTMLButtonElement, React.PropsWithChildren<Props>>(
  function({
    show,
    to,
    children,
    onClose,
    onClick,
    stickyTop = 0,
  }: React.PropsWithChildren<Props>, ref)
  {
    const isMounted = useHasMounted()

    const shouldRender = useDelayedTogglingBoolean(show, {
      offDelay: ANIMATION_DURATION_MS,
    })

    if (!shouldRender) {
      return null
    }

    if (!isMounted) {
      return null
    }

    const handleClose = (event: React.MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()

      onClose()
    }

    return (
      <Container style={{ '--prompt-card-sticky-top': rem(stickyTop + defaultTopMargin) } as React.CSSProperties}>
        <CardRoot to={to} className={cn({ isEntry: show })} onClick={onClick} ref={ref}>
          <CloseBlock>
            <IconButton
            kind="tertiary"
            onClick={handleClose}
            data-testid="sticky-card-close-button"
            size="small"
            shape="square"
          >
              <LineMultiplyIcon />
            </IconButton>
          </CloseBlock>
          {children}
        </CardRoot>
      </Container>
    )
  },
)

// @ts-ignore
StickyPromptCard.Header = HeaderGroup
// @ts-ignore
StickyPromptCard.Content = ContentGroup
// @ts-ignore
StickyPromptCard.Footer = FooterGroup

export default StickyPromptCard as typeof StickyPromptCard & {
  Header: typeof HeaderGroup;
  Content: typeof ContentGroup;
  Footer: typeof FooterGroup;
}
